import { normalizeRowsToShare } from '../../transforms/normalizeRowsToShare';
import { coerceChartRowSeriesValues } from '../coerceChartRowSeriesValues';
import { lineOrBarWideRows } from '../lineOrBarWideRows';
import {
  asChartData,
  axisNumberFormat,
  chartPresentationProps,
  isChartValueFormat,
  seriesToChartConfig,
} from '../presentation';
import type { ValueFormat } from '../presentation';
import type {
  LineOrBarAdapterConfig,
  LineOrBarAdapterProps,
  RenderAdapterResult,
} from '../types';
import { validateRowsInput } from '../validateRowsInput';

function isNumberFormatSpec(value: unknown): value is ValueFormat {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * A format that can ride on `presentation.passthrough` until a horizontal bar
 * moves it onto the value axis. Unknown strings are dropped rather than
 * forwarded as a token the renderer cannot resolve.
 */
function passthroughValueFormat(value: unknown): ValueFormat | undefined {
  if (isChartValueFormat(value) || isNumberFormatSpec(value)) return value;
  return undefined;
}

/**
 * hen-ui keeps data-axis names on both orientations. The dashboard stores
 * visual-axis space: `xAxis*` is the bottom axis and `yAxis*` is the left.
 * A horizontal bar draws values on the bottom, so those authored keys move
 * onto `yAxisFormat` / `yAxisLabel`, and the category keys move onto
 * `xAxisTickFormat` / `xAxisLabel`. Vertical bars stay in data-axis space.
 */
function barAxisProps(
  config: LineOrBarAdapterConfig | undefined,
): Record<string, unknown> {
  const passthrough = { ...(config?.presentation?.passthrough ?? {}) };
  if (passthrough.orientation !== 'horizontal') {
    return {
      ...passthrough,
      yAxisFormat: axisNumberFormat(config?.presentation?.yAxisFormat),
    };
  }

  const categoryFormat = config?.presentation?.yAxisFormat;
  const valueFormat = passthroughValueFormat(passthrough.xAxisTickFormat);
  const valueLabel = passthrough.xAxisLabel;
  const categoryLabel = passthrough.yAxisLabel;
  delete passthrough.xAxisTickFormat;
  delete passthrough.xAxisLabel;
  delete passthrough.yAxisLabel;
  delete passthrough.yAxisFormat;

  const swapped: Record<string, unknown> = { ...passthrough };
  const resolvedValueFormat = axisNumberFormat(valueFormat);
  if (resolvedValueFormat !== undefined) {
    swapped.yAxisFormat = resolvedValueFormat;
  }
  if (typeof valueLabel === 'string') swapped.yAxisLabel = valueLabel;
  // hen-ui resolves a tick format as a spec or a function, not an adapter token.
  const resolvedCategoryFormat = axisNumberFormat(categoryFormat);
  if (resolvedCategoryFormat !== undefined) {
    swapped.xAxisTickFormat = resolvedCategoryFormat;
  }
  if (typeof categoryLabel === 'string') swapped.xAxisLabel = categoryLabel;
  return swapped;
}

/**
 * Props for a chart that cannot draw: no rows, but the authored presentation
 * still applies so the chart renders its own empty state rather than a blank
 * frame with nothing configured.
 */
function emptyProps(
  config: LineOrBarAdapterConfig | undefined,
): LineOrBarAdapterProps {
  const series = config ? [...config.series] : [];
  return {
    ...barAxisProps(config),
    data: [],
    series,
    xAxisKey: config?.xAxisKey ?? '',
    config: seriesToChartConfig(series, config?.presentation?.series),
    ...chartPresentationProps(config?.presentation),
  };
}

export function barAdapter(
  rows: unknown,
  config: LineOrBarAdapterConfig,
): RenderAdapterResult<LineOrBarAdapterProps> {
  const validated = validateRowsInput(rows, emptyProps(config));
  if ('props' in validated) {
    return validated;
  }

  const wideResult = lineOrBarWideRows(validated.rows, config);
  if ('error' in wideResult) {
    return { props: emptyProps(config), error: wideResult.error };
  }

  const coerced = coerceChartRowSeriesValues(wideResult.rows, config.series);
  const data =
    config.pivotBy?.normalize === 'row-share'
      ? normalizeRowsToShare(coerced, [...config.series])
      : coerced;

  const series = [...config.series];

  return {
    props: {
      ...barAxisProps(config),
      data: asChartData(data),
      series,
      xAxisKey: config.xAxisKey,
      config: seriesToChartConfig(series, config.presentation?.series),
      ...chartPresentationProps(config.presentation),
    },
  };
}
